import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { authService, LoginRequest } from '../services/auth';
import { useAuthForm } from '../hooks/useAuthForm';
import { parseAuthError } from '../utils/authUtils';
import AuthPageLayout from '../components/ui/AuthPageLayout';
import FormField from '../components/ui/FormField';
import EmailField from '../components/ui/EmailField';
import AlertMessage from '../components/ui/AlertMessage';
import SubmitButton from '../components/ui/SubmitButton';
import AuthLink from '../components/ui/AuthLink';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useApp();

  const {
    formData,
    handleInputChange,
    isLoading,
    setIsLoading,
    error,
    setError,
    success,
  } = useAuthForm<LoginRequest>({
    email: '',
    password: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await authService.login(formData);
      dispatch({ type: 'SET_USER', payload: response.user });
      navigate('/dashboard');
    } catch (err: any) {
      const errorMessage = parseAuthError(
        err,
        'Login failed. Please check your credentials.'
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageLayout
      title="Sign in to your account"
      description={
        <>
          Or <AuthLink to="/signup">create a new account</AuthLink>
        </>
      }
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        <AlertMessage type="error" message={error} show={!!error} />
        <AlertMessage type="success" message={success} show={!!success} />

        <EmailField
          id="email"
          name="email"
          label="Email address"
          placeholder="Enter your email"
          value={formData.email}
          onChange={handleInputChange}
          required
          autoComplete="email"
        />

        <FormField
          id="password"
          name="password"
          type="password"
          label="Password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={handleInputChange}
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <AuthLink to="/forgot-password">Forgot your password?</AuthLink>
          </div>
        </div>

        <SubmitButton isLoading={isLoading}>Sign in</SubmitButton>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                Or continue with
              </span>
            </div>
          </div>

          <GoogleSignInButton mode="signin" onError={setError} />
        </div>
      </form>
    </AuthPageLayout>
  );
};

export default SignIn;
